import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate } from 'react-router-dom';
import {
  Activity, CheckCircle, AlertTriangle, Clock, ArrowRight,
  Upload, TrendingUp, BarChart3, Layers
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pillar configuration
const PILLARS = [
  { id: 'P1', name: 'Access & Engagement', color: 'blue', measures: 6 },
  { id: 'P2', name: 'Performance & Outcomes', color: 'green', measures: 3 },
  { id: 'P3', name: 'Efficiency & Investment', color: 'purple', measures: 2 },
  { id: 'P4', name: 'Strategic Alignment', color: 'amber', measures: 2 },
  { id: 'P5', name: 'Business Impact', color: 'red', measures: 2 }
];

const PILLAR_COLORS = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', progress: 'bg-blue-500', border: 'border-blue-200' },
  green: { bg: 'bg-emerald-50', text: 'text-emerald-700', progress: 'bg-emerald-500', border: 'border-emerald-200' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-700', progress: 'bg-purple-500', border: 'border-purple-200' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', progress: 'bg-amber-500', border: 'border-amber-200' },
  red: { bg: 'bg-rose-50', text: 'text-rose-700', progress: 'bg-rose-500', border: 'border-rose-200' }
};

export default function DataHealthWidget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dataHealth, setDataHealth] = useState({
    totalMeasures: 15,
    withData: 0,
    needsUpdate: 0,
    lastUpdated: null,
    pillarBreakdown: {}
  });

  useEffect(() => {
    fetchDataHealth();
  }, []);

  const fetchDataHealth = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Fetch all formulas and check which have data
      const response = await authedFetch(`${API_URL}/api/lei-components/formulas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const measures = data.measures || [];
        
        // Simulate data health check - in production this would come from the backend
        // For now, we'll check which measures have been imported
        let withDataCount = 0;
        const pillarData = {};
        
        // Initialize pillar data
        PILLARS.forEach(p => {
          pillarData[p.id] = { total: 0, withData: 0 };
        });

        // Count measures by pillar
        measures.forEach(m => {
          if (pillarData[m.pillar]) {
            pillarData[m.pillar].total++;
          }
        });

        // Try to get breakdown data for each measure to see if it has data
        for (const measure of measures) {
          try {
            const breakdownRes = await authedFetch(
              `${API_URL}/api/lei-components/breakdown/${measure.id}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (breakdownRes.ok) {
              const breakdown = await breakdownRes.json();
              if (breakdown.has_data) {
                withDataCount++;
                if (pillarData[measure.pillar]) {
                  pillarData[measure.pillar].withData++;
                }
              }
            }
          } catch (e) {
            // Ignore individual failures
          }
        }

        setDataHealth({
          totalMeasures: measures.length,
          withData: withDataCount,
          needsUpdate: measures.length - withDataCount,
          lastUpdated: new Date().toISOString(),
          pillarBreakdown: pillarData
        });
      }
    } catch (error) {
      logger.error('Error fetching data health:', error);
    }
    setLoading(false);
  };

  const completionPercent = dataHealth.totalMeasures > 0 
    ? Math.round((dataHealth.withData / dataHealth.totalMeasures) * 100)
    : 0;

  const getStatusColor = (percent) => {
    if (percent >= 80) return 'text-emerald-600';
    if (percent >= 50) return 'text-amber-600';
    return 'text-rose-600';
  };

  const getStatusLabel = (percent) => {
    if (percent >= 80) return 'Excellent';
    if (percent >= 50) return 'Good';
    if (percent > 0) return 'Needs Attention';
    return 'No Data';
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="animate-pulse flex items-center gap-2 text-slate-400">
              <Activity className="h-5 w-5 animate-spin" />
              <span>Loading data health...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200 overflow-hidden" data-testid="data-health-widget">
      <CardContent className="p-0">
        <div className="flex flex-col lg:flex-row">
          {/* Left Side - Overall Status */}
          <div className="flex-1 p-6 border-b lg:border-b-0 lg:border-r border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">LEI Data Health</h3>
                  <p className="text-xs text-slate-500">Component data coverage</p>
                </div>
              </div>
              <Badge 
                className={`${
                  completionPercent >= 80 ? 'bg-emerald-100 text-emerald-700' :
                  completionPercent >= 50 ? 'bg-amber-100 text-amber-700' :
                  'bg-rose-100 text-rose-700'
                }`}
              >
                {getStatusLabel(completionPercent)}
              </Badge>
            </div>

            {/* Main Stats */}
            <div className="flex items-center gap-6 mb-4">
              <div className="text-center">
                <p className={`text-4xl font-bold ${getStatusColor(completionPercent)}`}>
                  {dataHealth.withData}
                </p>
                <p className="text-xs text-slate-500">With Data</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-slate-300">/</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-slate-700">
                  {dataHealth.totalMeasures}
                </p>
                <p className="text-xs text-slate-500">Total Measures</p>
              </div>
              <div className="flex-1 ml-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">Coverage</span>
                  <span className={`font-semibold ${getStatusColor(completionPercent)}`}>
                    {completionPercent}%
                  </span>
                </div>
                <Progress value={completionPercent} className="h-3" />
              </div>
            </div>

            {/* Status Indicators */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <span className="text-sm text-emerald-800">
                  <strong>{dataHealth.withData}</strong> measures ready
                </span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-800">
                  <strong>{dataHealth.needsUpdate}</strong> need data
                </span>
              </div>
            </div>

            {/* CTA */}
            <Button 
              onClick={() => navigate('/component-data-import')}
              className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="update-data-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              {dataHealth.needsUpdate > 0 ? 'Import Component Data' : 'View Data'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Right Side - Pillar Breakdown */}
          <div className="flex-1 p-6 bg-slate-50/50">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="h-4 w-4 text-slate-500" />
              <h4 className="font-semibold text-slate-700 text-sm">Pillar Breakdown</h4>
            </div>

            <div className="space-y-3">
              {PILLARS.map(pillar => {
                const pillarStats = dataHealth.pillarBreakdown[pillar.id] || { total: pillar.measures, withData: 0 };
                const pillarPercent = pillarStats.total > 0 
                  ? Math.round((pillarStats.withData / pillarStats.total) * 100) 
                  : 0;
                const colors = PILLAR_COLORS[pillar.color];

                return (
                  <div key={pillar.id} className={`p-3 rounded-lg ${colors.bg} ${colors.border} border`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Badge className={`${colors.bg} ${colors.text} text-xs`}>
                          {pillar.id}
                        </Badge>
                        <span className="text-sm font-medium text-slate-700">{pillar.name}</span>
                      </div>
                      <span className={`text-xs font-semibold ${colors.text}`}>
                        {pillarStats.withData}/{pillarStats.total}
                      </span>
                    </div>
                    <div className="h-1.5 bg-white rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${colors.progress} transition-all duration-500`}
                        style={{ width: `${pillarPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {dataHealth.withData === 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700 flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5" />
                  <span>
                    <strong>Quick Start:</strong> Use Bulk Import to enter Q1 data for all measures at once
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
